import { LayoutDashboard, Database, Store, GitCompareArrows, Calculator, Library, UsersRound, Settings } from 'lucide-react';

import NavItem from './components/navigation/NavItem';
import NavFlyout from './components/navigation/NavFlyout';
import NavThemeMenu from './components/navigation/NavThemeMenu';
import HelpLink from './components/navigation/HelpLink';
import ProductLink from './components/navigation/ProductLink';
import { FEATURE_IDS } from '@/app/product/_enums';
import { useUserRoles } from '@/app/user/_hooks';
import ViewCompanyForNavigation from '@/app/company/ViewCompanyForNavigation';
import ToggleAgentPanelButtonForNavigation from '@/app/agentConversation/ToggleAgentPanelButtonForNavigation';
import UserAvatar from '@/app/user/UserAvatar';
import ListRecentComparisonsForNavigation from '@/app/recentComparison/ListRecentComparisonsForNavigation';
import ListRecentEstimatesForNavigation from '@/app/recentEstimate/ListRecentEstimatesForNavigation';
import { useCompany } from '@/data/company';

const Navigation = () => {
  const company = useCompany();
  const { isZebeler } = useUserRoles();

  // Same destinations and visibility rules as the former top header.
  const links = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testId: 'nav-link-dashboard', hidden: !company.data.is_analytics },
    { to: '/project', label: 'Project Database', icon: Database, testId: 'nav-link-project' },
    { to: '/market', label: 'Market', icon: Store, testId: 'nav-link-market', hidden: !company.data.is_market_data_visible },
    { to: '/comparison', label: 'Compare', icon: GitCompareArrows, testId: 'nav-link-comparison', flyout: ListRecentComparisonsForNavigation },
    {
      to: '/estimate',
      label: 'Estimate',
      icon: Calculator,
      testId: 'nav-link-estimate',
      flyout: ListRecentEstimatesForNavigation,
      hidden: !company.data.is_v2_estimate && !isZebeler
    },
    {
      to: '/unit-cost-history',
      label: 'Unit Cost History',
      icon: Library,
      testId: 'nav-link-unit-cost-catalog',
      featureId: FEATURE_IDS.UNIT_COST_HISTORY_NAV_ITEM
    },
    // Always shown — companies without precon management land on the upsell page. That is
    // also why it is the target of the Precon Team release tooltip: the rail item is the one
    // entry point that is mounted for every user on every page, entitled or not.
    {
      to: '/precon',
      label: 'Precon Team',
      icon: UsersRound,
      testId: 'nav-link-precon',
      locked: !company.data.is_precon_management,
      featureId: FEATURE_IDS.PRECON_TEAM_NAV_ITEM
    },
    { to: '/settings', label: 'Settings', icon: Settings, testId: 'nav-link-settings' }
  ];

  return (
    <nav data-testid="navigation" className="bg-sidebar fixed inset-y-0 left-0 z-50 flex w-(--app-nav-width) flex-col items-center gap-1 border-r py-3">
      <ViewCompanyForNavigation />

      <div className="bg-border my-2 h-px w-7" />

      <div className="flex flex-col items-center gap-1">
        {links
          .filter((link) => !link.hidden)
          .map((link) =>
            link.flyout ? (
              <NavFlyout key={link.to} to={link.to} label={link.label} icon={link.icon} testId={link.testId} Panel={link.flyout} />
            ) : (
              <NavItem key={link.to} to={link.to} label={link.label} icon={link.icon} testId={link.testId} locked={link.locked} featureId={link.featureId} />
            )
          )}
      </div>

      <div className="flex-1" />

      <div className="flex flex-col items-center gap-1">
        <ToggleAgentPanelButtonForNavigation />
        <ProductLink />
        <HelpLink />
        <NavThemeMenu />
        <div className="bg-border my-1 h-px w-7" />
        <UserAvatar />
      </div>
    </nav>
  );
};

export default Navigation;
